import React, { useState, useEffect } from "react";
import AdminSearchForm from "../../../forms/AdminSearchForm";
import axios from "axios";
import { useSelector } from "react-redux";
import moment from "moment";
import { useHistory, Link } from "react-router-dom";
import {
    canRetryPaym8,
    getPaym8RetryErrorMessage,
    getPaym8RetryMessageType,
    retryPaym8Order,
} from "../payments/paym8Retry";

export default function Orders() {
    const { currentUser } = useSelector((state) => state.auth);
    const history = useHistory();

    const [orders, setOrders] = useState([]);
    const [pageNumber, setPageNumber] = useState(0);
    const [recordCount, setRecordCount] = useState(0);
    const [sortBy, setSortBy] = useState("EntrySystemDate"); // Default sorting
    const [sortDirection, setSortDirection] = useState("desc"); // Default sorting direction
    const [pageSizeValue, setPageSizeValue] = useState(10);
    const [filterValue, setFilterValue] = useState("");
    const [retryingOrderId, setRetryingOrderId] = useState("");
    const [retryMessages, setRetryMessages] = useState({});
    // Distinguish "still fetching" and "fetch failed" from "no orders" -
    // this page previously rendered an empty table with "0 of 0 results"
    // while the (slow) request was in flight, which customers read as
    // their order history being gone.
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        getOrders();
    }, [sortBy, sortDirection, filterValue, pageNumber, pageSizeValue]);

    async function getOrders() {
        setIsLoading(true);
        setLoadError(false);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}all-orders`,
                {
                    userId: currentUser.id,
                    customer_code:
                        currentUser.isCustomer === 1 &&
                            currentUser.customer_code !== null
                            ? currentUser.customer_code
                            : currentUser.customer_code === null ||
                                currentUser.customer_code.split(",").length > 1
                                ? "None"
                                : currentUser.customer_code,
                    pageNumber: pageNumber,
                    pageSizeValue: pageSizeValue,
                    sortBy: sortBy,
                    sortDirection: sortDirection,
                    filterItem: filterValue,
                },
                { headers: { Authorization: `Bearer ${currentUser.token}` } }
            );
            // console.log(response.data);
            const orderList = response.data[0];
            const resultOrder = response.data[2].map((a) => a.order_no);
            const ownOrders = response.data[2];
            setRecordCount(response.data[1][0][""]);

            let allOrders = [];

            orderList.forEach((order) => {
                // Determine the status name
                let statusName = "";
                switch (order.OrderStatus) {
                    case "1":
                        statusName = "Open Order";
                        break;
                    case "2":
                        statusName = "Open Back Order";
                        break;
                    case "3":
                        statusName = "Released back order";
                        break;
                    case "4":
                        statusName = "In warehouse";
                        break;
                    case "8":
                        statusName = "To invoice";
                        break;
                    case "F":
                        statusName = "Forward order";
                        break;
                    case "S":
                        statusName = "In Suspense";
                        break;
                    case "9":
                        statusName = "Complete";
                        break;
                    case "\\":
                        statusName = "Cancelled";
                        break;
                    default:
                        statusName = "Unknown";
                }

                let status = "";
                if (order.ActiveFlag === "N") {
                    status = `Inactive (${statusName})`;
                } else if (order.CancelledFlag === "Y") {
                    status = `Cancelled (${statusName})`;
                } else {
                    status = `(${statusName})`;
                }

                // Determine who placed the order
                let placed_by = "Inhouse";
                const trackedOrder = ownOrders.find((ownOrder) => ownOrder.order_no === order.SalesOrder);
                if (resultOrder.includes(order.SalesOrder) && trackedOrder) {
                    placed_by =
                        trackedOrder.user?.is_sales === 1
                            ? `Rep - ${trackedOrder.user?.email || ""}`
                            : `Customer - ${trackedOrder.user?.email || ""}`;
                }

                allOrders.push({
                    SalesOrder: order.SalesOrder,
                    CustomerPoNumber: order.CustomerPoNumber || "",
                    EntrySystemDate: moment(order.EntrySystemDate).format(
                        "YYYY-MM-DD"
                    ),
                    OrderStatus: status,
                    Total: `R ${order.orderCost}`,
                    PlacedBy: placed_by,
                    latest_payment: trackedOrder?.latest_payment || order.latest_payment,
                    payment_retry: trackedOrder?.payment_retry || order.payment_retry,
                });
            });
            // console.log(allOrders);
            setOrders(allOrders);
        } catch (error) {
            console.error("Error fetching orders:", error);
            setLoadError(true);
        } finally {
            setIsLoading(false);
        }
    }

    function getStatusName(orderStatus) {
        const statusMap = {
            1: "Open Order",
            2: "Open Back Order",
            3: "Released back order",
            4: "In warehouse",
            8: "To invoice",
            9: "Complete",
            F: "Forward order",
            S: "In Suspense",
            "\\": "Cancelled",
        };
        return statusMap[orderStatus] || "Unknown";
    }

    function getPlacedBy(order, ownOrders) {
        const placedBy = ownOrders.find((o) => o.order_no === order.SalesOrder);
        return placedBy
            ? placedBy.user.is_sales === 1
                ? `Rep - ${placedBy.user.email}`
                : `Customer - ${placedBy.user.email}`
            : "Inhouse";
    }

    const handleSort = (field) => {
        const newSortDirection =
            sortBy === field && sortDirection === "asc" ? "desc" : "asc";
        setSortBy(field);
        setSortDirection(newSortDirection);
    };

    async function handleRetryPayment(event, order) {
        event.stopPropagation();

        if (!canRetryPaym8(order.payment_retry) || retryingOrderId) {
            return;
        }

        setRetryingOrderId(order.SalesOrder);
        setRetryMessages((messages) => ({
            ...messages,
            [order.SalesOrder]: null,
        }));

        try {
            const response = await retryPaym8Order({
                paymentRetry: order.payment_retry,
                orderId: order.SalesOrder,
                token: currentUser.token,
            });

            if (response.data?.redirect_url) {
                window.location.href = response.data.redirect_url;
                return;
            }

            setRetryMessages((messages) => ({
                ...messages,
                [order.SalesOrder]: {
                    type: "warning",
                    text: "Payment retry did not return a PayM8 redirect.",
                },
            }));
        } catch (error) {
            setRetryMessages((messages) => ({
                ...messages,
                [order.SalesOrder]: {
                    type: getPaym8RetryMessageType(error),
                    text: getPaym8RetryErrorMessage(error),
                },
            }));
        } finally {
            setRetryingOrderId("");
        }
    }

    function getRetryMessageClass(type) {
        if (type === "success") {
            return "text-green-700";
        }

        if (type === "warning") {
            return "text-yellow-700";
        }

        return "text-red-700";
    }

    return (
        <div className="bg-gray-50">
            <AdminSearchForm
                heading="Orders"
                breadcrumbs={[
                    { name: "Home", href: "/", count: 1 },
                    { name: "Orders", href: "/orders", count: 2 },
                ]}
            />

            {/* <AdminNav cartTotal={cartTotal}/> */}
            <br />
            <br />

            <div className="container mx-auto relative pt-20">
                <div className="flex absolute top-0 right-0 mt-5">
                    <Link
                        as="a"
                        to={"/backorders"}
                        className="hover:text-lk_dark_grey_button_hover"
                    >
                        <button className="px-10 py-2 border border-transparent text-base font-medium border_radius_4 shadow-sm text-white bg-lk_red hover:bg-lk_red_button_hover uppercase mx-auto">
                            View all items on Backorder
                        </button>
                    </Link>
                </div>
                <label className="mt-2 mr-2">Filter: </label>
                <input
                    type="text"
                    onChange={(v) => setFilterValue(v.target.value)}
                />
                <br />
                <small>
                    When filtering by date please enter in YYYY-MM-DD format
                </small>
            </div>

            <div className="container mx-auto">
                <table
                    className="mb-10 w-full"
                    style={{
                        borderCollapse: "separate",
                        borderSpacing: "0 1em",
                    }}
                >
                    <thead className="bg-gray-200">
                        <tr>
                            <th
                                className="px-2 py-5 font-light text-sm cursor-pointer"
                                onClick={() => handleSort("SalesOrder")}
                            >
                                Order No.
                            </th>
                            <th
                                className="px-2 py-5 font-light text-sm cursor-pointer"
                                onClick={() => handleSort("CustomerPoNumber")}
                            >
                                Product Order Number
                            </th>
                            <th
                                className="px-2 py-5 font-light text-sm cursor-pointer"
                                onClick={() => handleSort("EntrySystemDate")}
                            >
                                Order Date
                            </th>
                            <th
                                className="px-2 py-5 font-light text-sm cursor-pointer"
                                onClick={() => handleSort("OrderStatus")}
                            >
                                Order Status
                            </th>
                            <th className="px-2 py-5 font-light text-sm">
                                Placed By
                            </th>
                            <th className="px-2 py-5 font-light text-sm">
                                Total (Excl vat)
                            </th>
                            <th className="px-2 py-5 font-light text-sm">
                                Payment
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && (
                            <tr className="bg-white shadow-lg">
                                <td colSpan={7} className="text-center px-2 py-6 text-gray-500">
                                    Loading your orders&hellip;
                                </td>
                            </tr>
                        )}
                        {!isLoading && loadError && (
                            <tr className="bg-white shadow-lg">
                                <td colSpan={7} className="text-center px-2 py-6">
                                    <span className="text-lk_red font-medium">
                                        We couldn't load your orders.
                                    </span>{" "}
                                    <button
                                        type="button"
                                        className="underline text-lk_red"
                                        onClick={() => getOrders()}
                                    >
                                        Try again
                                    </button>
                                </td>
                            </tr>
                        )}
                        {!isLoading && !loadError && orders.length === 0 && (
                            <tr className="bg-white shadow-lg">
                                <td colSpan={7} className="text-center px-2 py-6 text-gray-500">
                                    No orders found.
                                </td>
                            </tr>
                        )}
                        {!isLoading && orders.map((order, index) => (
                            <tr
                                key={index}
                                className="bg-white mt-5 shadow-lg cursor-pointer"
                                onClick={() =>
                                    history.push(`/order/${order.SalesOrder}`)
                                }
                            >
                                <td className="mx-auto text-center relative px-2 py-2">
                                    {order.SalesOrder}
                                </td>
                                <td className="mx-auto text-center relative px-2 py-2">
                                    {order.CustomerPoNumber}
                                </td>
                                <td className="mx-auto text-center relative px-2 py-2">
                                    {order.EntrySystemDate}
                                </td>
                                <td className="mx-auto text-center relative px-2 py-2">
                                    {order.OrderStatus}
                                </td>
                                <td className="mx-auto text-center relative px-2 py-2">
                                    {order.PlacedBy}
                                </td>
                                <td className="mx-auto text-center relative px-2 py-2">
                                    {order.Total}
                                </td>
                                <td className="mx-auto text-center relative px-2 py-2">
                                    {canRetryPaym8(order.payment_retry) && (
                                        <button
                                            type="button"
                                            className="px-4 py-2 border border-transparent text-sm font-medium border_radius_4 shadow-sm text-white bg-lk_red hover:bg-lk_red_hover uppercase disabled:opacity-50"
                                            disabled={retryingOrderId === order.SalesOrder}
                                            onClick={(event) => handleRetryPayment(event, order)}
                                        >
                                            {retryingOrderId === order.SalesOrder ? "Retrying..." : "Retry payment"}
                                        </button>
                                    )}

                                    {retryMessages[order.SalesOrder]?.text && (
                                        <div className={`mt-2 text-xs font-medium ${getRetryMessageClass(retryMessages[order.SalesOrder].type)}`}>
                                            {retryMessages[order.SalesOrder].text}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <nav
                    className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 bg-gray-50"
                    aria-label="Pagination"
                >
                    <div className="hidden sm:block">
                        <p className="text-sm text-gray-700">
                            Page{" "}
                            <span className="font-medium">
                                {pageNumber + 1}
                            </span>{" "}
                            of{" "}
                            <span className="font-medium">
                                {Math.ceil(recordCount / pageSizeValue)}
                            </span>
                        </p>
                        <p className="text-sm text-gray-700">
                            Showing{" "}
                            <span className="font-medium">
                                {pageNumber === 0
                                    ? "1"
                                    : pageNumber * pageSizeValue + 1}
                            </span>{" "}
                            to{" "}
                            <span className="font-medium">
                                {Math.min(
                                    (pageNumber + 1) * pageSizeValue,
                                    recordCount
                                )}
                            </span>{" "}
                            of{" "}
                            <span className="font-medium">{recordCount}</span>{" "}
                            results
                        </p>
                    </div>
                    <div className="flex-1 flex justify-between sm:justify-end">
                        <button
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            onClick={() =>
                                setPageNumber((prev) => Math.max(prev - 1, 0))
                            }
                            disabled={pageNumber === 0}
                        >
                            Previous
                        </button>
                        <button
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            onClick={() => setPageNumber((prev) => prev + 1)}
                            disabled={
                                (pageNumber + 1) * pageSizeValue >= recordCount
                            }
                        >
                            Next
                        </button>
                    </div>
                </nav>
            </div>
        </div>
    );
}
